import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import kebabCase from 'lodash/kebabCase';
import snakeCase from 'lodash/snakeCase';

import Tooltip from 'components/Tooltip';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import InstagramDataEnabled from 'components/instagram-data-enabled/InstagramDataEnabled';
import MoreButton from 'components/buttons/more-button/MoreButton';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';

import DeliverableMediaAttachments from 'modules/deliverables/components/media/parts/DeliverableMediaAttachments';
import SelectStorySnippets, {
  deleteStorySnippets,
  getStoriesForDeliverable
} from 'modules/select-story-snippets';
import DeliverableMediaAttachment from 'modules/deliverables/components/media/parts/DeliverableMediaAttachment';

import { toggleDataEnabled } from 'components/instagram-data-enabled/redux/actions';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { __t, applyTranslations } from 'utils/language';
import { addThousandsSeparator } from 'utils/helper/common';
import { isAdmin } from 'utils/account';
import {
  COMMON_THUMBNAIL_DIMENSION,
  EMPTY_POST,
  EMPTY_REEL,
  EMPTY_STORY,
  THUMBNAIL_SIZES
} from '../utils/metrics-temp-fb';

import './metrics-temp-fb-info.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./metrics-temp-fb-info.i18n.json'));

import type {
  AdminDeliverable,
  AssetMediaFormat,
  DeliverableAttachmentType
} from 'types';
import type {
  MetricsTempFBDeliverableTypeShort,
  MetricsTempFBGenericType,
  MetricsTempFBKpi,
  MetricsTempFBMetricItem
} from '../types';
import type {
  StorySnippet
} from 'modules/select-story-snippets/types';

interface ConfirmationModalParams {
  title: string,
  message: string,
  onConfirm: () => void,
  onCancel: () => void
}

interface OwnProps {
  deliverable: AdminDeliverable,
  hasActiveFbConnection: boolean,
  onDataToggle: (deliverableId: number) => void
}

interface StateProps {
  isAdminMode: boolean
}

interface DispatchProps {
  dispatchToggleDataEnabled: (
    offerId: number,
    deliverableId: number,
    dataEnabled: boolean
  ) => Promise<boolean>,
  dispatchShowConfirmationModal: (
    params: ConfirmationModalParams
  ) => void,
  dispatchHideConfirmationModal: () => void
}

type Props = OwnProps & StateProps & DispatchProps;

interface State {
  height: number,
  storySnippets: Array<StorySnippet>,
  posts: any, // TODO: update this once BE provides tech specs
  reels: any, // TODO: update this once BE provides tech specs
  isPost: boolean,
  isReel: boolean,
  isStory: boolean,
  deliverableTypeShort: MetricsTempFBDeliverableTypeShort
}

const ContextMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  forceEventPropagation: true
});

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'metrics_temp_fb_info';

class MetricsTempFBInfo extends React.Component<Props, State> {
  state: State = {
    height: 0,
    storySnippets: [],
    posts: [],
    reels: [],
    isPost: false,
    isReel: false,
    isStory: false,
    deliverableTypeShort: '' as MetricsTempFBDeliverableTypeShort
  };

  async componentDidMount() {
    const { deliverable } = this.props;
    const deliverableTypeShort = (deliverable.deliverableType || '')
      .replace(/(^[a-z]+_)/, '') as MetricsTempFBDeliverableTypeShort;

    const isPost = deliverableTypeShort.includes('post');
    const isReel = deliverableTypeShort === 'reel';
    const isStory = deliverableTypeShort.includes('story');

    this.setState({
      posts: isPost ? deliverable.analytics : [],
      reels: isReel ? deliverable.analytics : [],
      storySnippets: isStory ? await getStoriesForDeliverable(deliverable.id) : [],
      isPost,
      isReel,
      isStory,
      deliverableTypeShort
    }, this.setHeight);
  }

  private setHeight = (): void => (
    this.setState(state => ({
      height: this.shouldRenderEmptyRow()
        ? COMMON_THUMBNAIL_DIMENSION
        : THUMBNAIL_SIZES[state.deliverableTypeShort].height
    }))
  );

  private getAssetType = (
    item: MetricsTempFBMetricItem
  ): AssetMediaFormat => {
    const isVideo = item.storyType === 'video';

    return isVideo || (!isVideo && !isNil(item.videoUrl))
      ? 'video'
      : 'image';
  };

  private getContextMenuItems(): Array<any> {
    return [{
      name: 'toggle-data-enabled',
      text: __t(`${MSG_KEY}.context_menu.disable_ig_data`),
      onClick: this.handleToggleDataEnabled
    }];
  }

  private shouldRenderEmptyRow = (): boolean => {
    const { posts, storySnippets, reels, isPost, isReel, isStory } = this.state;

    return (isPost && isEmpty(posts))
      || (isReel && isEmpty(reels))
      || (isStory && isEmpty(storySnippets));
  };

  private handleToggleDataEnabled = (): void => {
    const {
      deliverable,
      onDataToggle,
      dispatchToggleDataEnabled,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal
    } = this.props;

    dispatchShowConfirmationModal({
      title: __t(`${MSG_KEY}.modals.confirm.title`),
      message: __t(`${MSG_KEY}.modals.confirm.message`),
      onConfirm: () => {
        dispatchToggleDataEnabled(
          deliverable.offerId,
          deliverable.id,
          !deliverable.isFacebookDataEnabled
        ).then(() => {
          onDataToggle(deliverable.id);
          dispatchHideConfirmationModal();
        });
      },
      onCancel: () => dispatchHideConfirmationModal()
    });
  };

  private handleSelection = (
    storySnippets: Array<StorySnippet>
  ): void => {
    this.setState({
      storySnippets
    }, this.setHeight);
  };

  private handleDelete = async (
    event: React.SyntheticEvent<HTMLDivElement>
  ): Promise<void> => {
    const { deliverable } = this.props;
    const target = event.currentTarget;
    const code = target.dataset.code || null;

    if (isNil(code)) {
      return;
    }

    this.setState({
      storySnippets: await deleteStorySnippets(deliverable.id, [code])
    }, this.setHeight);
  };

  private renderEmptySnippet = (): React.ReactNode => (
    <div
      className="metrics-temp-fb-info__snippet-empty"
      style={{ height: this.state.height }}
    >
      {__t(`${MSG_KEY}.labels.nothing_here`)}
    </div>
  );

  private renderContextMenu(): React.ReactNode {
    const menuItems = this.getContextMenuItems();

    return (
      <div className="metrics-temp-fb-info__context-menu">
        <ContextMenu
          contextMenuProps={{
            items: menuItems
          }}
          width={24}
        />
      </div>
    );
  }

  private renderThumbnail = (
    item: MetricsTempFBMetricItem
  ): React.ReactNode => {
    const { deliverable } = this.props;
    const { isPost, isStory, isReel, deliverableTypeShort } = this.state;

    if (this.shouldRenderEmptyRow()) {
      return this.renderEmptySnippet();
    }

    const assetType = this.getAssetType(item);
    const postAssetUrl = (isPost && deliverable.post?.thumbnailUrl) || null;
    const reelAssetUrl = (isReel && deliverable.post?.url) || null;
    const reelThumbnailUrl = (isReel && deliverable.post?.thumbnailUrl) || null;
    const storyAssetUrl = isStory && assetType === 'video'
      ? item.videoUrl
      : item.imageUrl;

    const attachment = {
      id: item.code,
      assetType: assetType as AssetMediaFormat,
      createdAt: item.takenAt || '',
      meta: { type: 'highres' as DeliverableAttachmentType },
      imageUrl: postAssetUrl || item.imageUrl,
      thumbnailUrl: reelThumbnailUrl || postAssetUrl || item.imageUrl,
      externalUrl: reelAssetUrl,
      url: reelThumbnailUrl || postAssetUrl || storyAssetUrl || item.imageUrl
    };

    if (isReel) {
      return (
        <DeliverableMediaAttachment
          width={THUMBNAIL_SIZES[deliverableTypeShort].width}
          height={THUMBNAIL_SIZES[deliverableTypeShort].height}
          attachment={attachment}
          isImagePreviewEnabled
        />
      );
    }

    return (
      <div className="metrics-temp-fb-info__thumbnail">
        <DeliverableMediaAttachments
          mediaWidth={THUMBNAIL_SIZES[deliverableTypeShort].width}
          mediaHeight={THUMBNAIL_SIZES[deliverableTypeShort].height}
          attachments={[attachment]}
          canUpload={false}
          compactDisplay
          isImagePreviewEnabled
          useAssetDirectly
        />
      </div>
    );
  };

  private renderActionsTooltip = (
    children: React.ReactNode
  ): React.ReactNode => (
    <Tooltip
      width="auto-compact"
      message={__t(`${COMMON_MSG_KEY}.basics.delete`)}
    >
      {children}
    </Tooltip>
  );

  private renderActions = (
    item: MetricsTempFBMetricItem
  ): React.ReactNode => {
    const { storySnippets, height } = this.state;
    const isDisabled = isEmpty(storySnippets);

    const Icon = (
      <SVGDynamicIcon
        name="trash-custom-fg"
        width={12}
        height={12}
        disabled={isDisabled}
        onClick={this.handleDelete}
        data={{ code: item.code }}
      />
    );

    return (
      <div
        className="metrics-temp-fb-info__actions metrics-temp-fb-info__column-value"
        style={{ height }}
      >
        {isDisabled ? Icon : this.renderActionsTooltip(Icon)}
      </div>
    );
  };

  private renderIndexColumn = (
    index: number
  ): React.ReactNode => (
    <div
      className="metrics-temp-fb-info__column metrics-temp-fb-info__column--index"
      style={{ height: this.state.height }}
    >
      {index + 1}
    </div>
  );

  private renderGenericColumn = (
    type: MetricsTempFBGenericType,
    item: MetricsTempFBMetricItem
  ): React.ReactNode => (
    <div className="metrics-temp-fb-info__column">
      <div className="metrics-temp-fb-info__column-header">
        {__t(`${MSG_KEY}.labels.${snakeCase(type)}`)}
      </div>
      {type !== 'actions' && this.renderThumbnail(item)}
      {type === 'actions' && this.renderActions(item)}
    </div>
  );

  private renderMetricColumn = (
    name: MetricsTempFBKpi,
    value: number | null | undefined
  ): React.ReactNode => {
    const { height } = this.state;
    const nameKebabCase = kebabCase(name);
    const missingText = __t(`${MSG_KEY}.labels.missing`);

    const columnClasses = classnames('metrics-temp-fb-info__column', {
      [`metrics-temp-fb-info__column--name-${nameKebabCase}`]: true
    });

    const headerClasses = classnames('metrics-temp-fb-info__column-header', {
      [`metrics-temp-fb-info__column-header--name-${nameKebabCase}`]: true
    });

    const valueClasses = classnames('metrics-temp-fb-info__column-value', {
      [`metrics-temp-fb-info__column-value--name-${nameKebabCase}`]: true
    });

    return (
      <div className={columnClasses}>
        <div className={headerClasses}>
          {__t(`${COMMON_MSG_KEY}.kpis.${snakeCase(name)}`)}
        </div>
        <div
          className={valueClasses}
          style={{ height }}
        >
          {addThousandsSeparator(value ?? missingText)}
        </div>
      </div>
    );
  };

  private renderPostRow = (
    item: MetricsTempFBMetricItem
  ): React.ReactNode => (
    <div
      key={`metrics-temp-fb-info__row--post-${item.code}`}
      className="metrics-temp-fb-info__row metrics-temp-fb-info__row--post"
    >
      {this.renderGenericColumn('post', item)}
      {this.renderMetricColumn('reach', item.reach)}
      {this.renderMetricColumn('impressions', item.impressions)}
      {this.renderMetricColumn('interactions', item.interactions)}
    </div>
  );

  private renderStoryRow = (
    item: MetricsTempFBMetricItem,
    index: number
  ): React.ReactNode => (
    <div
      key={`metrics-temp-fb-info__row--story-${item.code}`}
      className="metrics-temp-fb-info__row metrics-temp-fb-info__row--story"
    >
      {this.renderIndexColumn(index)}
      {this.renderGenericColumn('story', item)}
      {this.renderMetricColumn('reach', item.reach)}
      {this.renderMetricColumn('impressions', item.impressions)}
      {this.renderMetricColumn('tapsForward', item.tapsForward)}
      {this.renderMetricColumn('tapsBack', item.tapsBack)}
      {this.renderMetricColumn('exits', item.exits)}
      {this.renderGenericColumn('actions', item)}
    </div>
  );

  private renderReelRow = (
    item: MetricsTempFBMetricItem
  ): React.ReactNode => (
    <div
      key={`metrics-temp-fb-info__row--reel-${item.code}`}
      className="metrics-temp-fb-info__row metrics-temp-fb-info__row--reel"
    >
      {this.renderGenericColumn('reel', item)}
      {this.renderMetricColumn('reach', item.reach)}
      {this.renderMetricColumn('impressions', item.impressions) /* impressions === plays per Mikael */ }
      {this.renderMetricColumn('totalInteractions', item.interactions)}
    </div>
  );

  private renderSelectSnippets = (): React.ReactNode => {
    const { deliverable } = this.props;
    const { campaign } = deliverable;

    return (
      <div className="metrics-temp-fb-info__snippets">
        <div className="metrics-temp-fb-info__snippets-header">
          {__t(`${MSG_KEY}.labels.story_snippets`)}
        </div>
        <div className="metrics-temp-fb-info__snippets-body">
          {__t(`${MSG_KEY}.messages.select_snippets`)}
        </div>
        {
          !isNil(campaign?.startDate) && (
            <div className="metrics-temp-fb-info__snippets-button">
              <SelectStorySnippets
                deliverableId={deliverable.id}
                campaignStartDate={campaign.startDate}
                onSelect={this.handleSelection}
              />
            </div>
          )
        }
      </div>
    );
  };

  render(): React.ReactNode {
    const { deliverable, hasActiveFbConnection, isAdminMode } = this.props;
    const { posts, storySnippets, reels, isPost, isReel, isStory } = this.state;

    return (
      <div className="metrics-temp-fb-info">
        <div className="metrics-temp-fb-info__data-badge">
          <InstagramDataEnabled
            hasActiveFbConnection={hasActiveFbConnection}
            header={{ show: true }}
            defaultText={{
              show: true,
              text: __t(`${MSG_KEY}.messages.default_${deliverable.deliverableType}`)
            }}
            additionalText={{
              text: __t(`${MSG_KEY}.messages.not_editable`)
            }}
            contextMenu={isAdminMode ? this.renderContextMenu() : undefined}
            flex="row"
          />
        </div>
        <div className="metrics-temp-fb-info__rows">
          {isPost && isEmpty(posts) && this.renderPostRow(EMPTY_POST)}
          {isReel && isEmpty(reels) && this.renderReelRow(EMPTY_REEL)}
          {isStory && isEmpty(storySnippets) && this.renderStoryRow(EMPTY_STORY, 0)}
          {isPost && posts.map(this.renderPostRow)}
          {isReel && reels.map(this.renderReelRow)}
          {isStory && storySnippets.map(this.renderStoryRow)}
        </div>
        {isStory && this.renderSelectSnippets()}
      </div>
    );
  }
}

const mapStateToProps = ({ account }): StateProps => ({
  isAdminMode: isAdmin(account.currentAccount, account.roles)
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchToggleDataEnabled: (offerId: number, deliverableId: number, dataEnabled: boolean) => (
    dispatch(toggleDataEnabled(offerId, deliverableId, dataEnabled))
  ),
  dispatchShowConfirmationModal: (params: ConfirmationModalParams) => (
    dispatch(showConfirmModal(params))
  ),
  dispatchHideConfirmationModal: () => (
    dispatch(hideConfirmModal())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(MetricsTempFBInfo);
